import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { IcArrowLeft, IcPrinter, IcPackage, IcSparkles } from '../../theme/icons';
import saleService from '../../services/saleService';
import { useCompany } from '../../context/CompanyContext';
import { printDocument } from '../../desktop/engine';
import { CardSkeleton } from '../../components/Skeleton';

const SaleReceipt = () => {
  const { id } = useParams();
  const { settings: company } = useCompany();
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSale = async () => {
      try {
        setLoading(true);
        console.log('Fetching sale with ID:', id);
        const data = await saleService.getSaleById(id);
        console.log('Sale data received:', data);
        setSale(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching sale:', err);
        console.error('Error details:', err.response || err.message);
        setError('Failed to load sale details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchSale();
    } else {
      setError('No sale ID provided');
      setLoading(false);
    }
  }, [id]);

  const handlePrint = () => {
    // Desktop app: silent print on the ticket printer of this PC; web: dialog.
    printDocument('ticket');
  };

  // Format date to readable format
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  // Format currency to DT
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '0.000 DT';
    return `${parseFloat(amount).toFixed(3)} DT`;
  };

  if (loading) {
    return (
      <div className="text-center py-10">
        <CardSkeleton />
      </div>
    );
  }

  if (error || !sale) {
    return (
      <div className="w-full px-4 pt-2 pb-4">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
          <p>{error || 'Vente non trouvée'}</p>
        </div>
        <Link to="/seller-history" className="btn-s px-5 py-2.5 text-sm">
          <IcArrowLeft className="mr-2" />
          Retour
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full px-4 pt-2 pb-4">
      {/* Action Buttons */}
      <div className="mb-6 flex justify-between items-center no-print">
        <Link to="/seller-history" className="btn-s px-5 py-2.5 text-sm">
          <IcArrowLeft className="mr-2" />
          Retour
        </Link>
        <button
          onClick={handlePrint}
          className="btn-s py-2 px-6 transition-all"
        >
          <IcPrinter className="mr-2" />
          Imprimer le reçu
        </button>
      </div>

      {/* Receipt Content */}
      <div className="bg-white p-8 w-full border border-gray-200 print:border-0">
        {/* Header */}
        <div className="text-center border-b-2 border-gray-800 pb-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2 uppercase">{(company && company.nom) || 'Kipara'}</h1>
          <p className="text-gray-600">{sale.is_return ? 'Bon de retour' : 'Reçu de vente'}</p>
          {company && company.adresse && (
            <p className="text-sm text-gray-500 mt-2 whitespace-pre-line">{company.adresse}</p>
          )}
          {company && ((company.telephones && company.telephones.length > 0) || company.matricule_fiscale) && (
            <p className="text-sm text-gray-500">
              {company.telephones && company.telephones.length > 0 && <span>Tél: {company.telephones.join(' · ')}</span>}
              {company.telephones && company.telephones.length > 0 && company.matricule_fiscale && <span className="mx-2">·</span>}
              {company.matricule_fiscale && <span>MF: {company.matricule_fiscale}</span>}
            </p>
          )}
        </div>

        {/* Sale Info */}
        <div className="grid grid-cols-2 gap-6 mb-8">
          <div>
            <h3 className="font-bold text-gray-700 mb-3 text-lg border-b pb-2">Informations de vente</h3>
            <div className="space-y-2 text-sm">
              <p><span className="font-semibold text-gray-600">N° de vente:</span> <span className="font-bold text-gray-900">#{sale.id}</span></p>
              <p><span className="font-semibold text-gray-600">Date:</span> {formatDate(sale.date_added || sale.date)}</p>
              {sale.seller_name && <p><span className="font-semibold text-gray-600">Vendeur:</span> {sale.seller_name}</p>}
            </div>
          </div>
          <div>
            <h3 className="font-bold text-gray-700 mb-3 text-lg border-b pb-2">Client</h3>
            <div className="space-y-2 text-sm">
              <p><span className="font-semibold text-gray-600">Nom:</span> {sale.customer_name || sale.customer?.name || 'Client non spécifié'}</p>
            </div>
          </div>
        </div>

        {/* Products Section */}
        {(sale.details && sale.details.length > 0) && (
          <div className="mb-6">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2 text-lg border-b-2 border-blue-200 pb-2">
              <IcPackage className="text-blue-600" />
              Produits
            </h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left py-2 font-semibold text-gray-700">#</th>
                  <th className="text-left py-2 font-semibold text-gray-700">Produit</th>
                  <th className="text-right py-2 font-semibold text-gray-700">Prix unitaire</th>
                  <th className="text-center py-2 font-semibold text-gray-700">Qté</th>
                  <th className="text-right py-2 font-semibold text-gray-700">Total</th>
                </tr>
              </thead>
              <tbody>
                {sale.details.map((item, index) => (
                  <tr key={item.id} className="border-b border-gray-200">
                    <td className="py-3 text-gray-600">{index + 1}</td>
                    <td className="py-3 font-medium text-gray-800">{item.product_name || item.product?.name || 'N/A'}</td>
                    <td className="py-3 text-right text-gray-700">{formatCurrency(item.price)}</td>
                    <td className="py-3 text-center text-gray-700">{Math.abs(item.quantity || 1)}</td>
                    <td className="py-3 text-right font-semibold text-gray-900">{formatCurrency(Math.abs(item.total_detail || (item.price * item.quantity)))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Services Section */}
        {(sale.service_details && sale.service_details.length > 0) && (
          <div className="mb-6">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2 text-lg border-b-2 border-green-200 pb-2">
              <IcSparkles className="text-green-600" />
              Services
            </h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left py-2 font-semibold text-gray-700">#</th>
                  <th className="text-left py-2 font-semibold text-gray-700">Service</th>
                  <th className="text-right py-2 font-semibold text-gray-700">Prix</th>
                  <th className="text-center py-2 font-semibold text-gray-700">Remise</th>
                  <th className="text-center py-2 font-semibold text-gray-700">Qté</th>
                  <th className="text-right py-2 font-semibold text-gray-700">Total</th>
                </tr>
              </thead>
              <tbody>
                {sale.service_details.map((service, index) => {
                  const price = parseFloat(service.price) || 0;
                  const remise = parseFloat(service.remise) || 0;
                  const finalPrice = remise > 0 ? price * (1 - remise / 100) : price;
                  
                  return (
                    <tr key={service.id} className="border-b border-gray-200">
                      <td className="py-3 text-gray-600">{index + 1}</td>
                      <td className="py-3 font-medium text-gray-800">{service.service_name || service.service?.name || 'N/A'}</td>
                      <td className="py-3 text-right text-gray-700">
                        {remise > 0 ? (
                          <div>
                            <div className="font-semibold text-green-700">{formatCurrency(finalPrice)}</div>
                            <div className="text-xs text-gray-400 line-through">{formatCurrency(price)}</div>
                          </div>
                        ) : (
                          formatCurrency(price)
                        )}
                      </td>
                      <td className="py-3 text-center">
                        {remise > 0 ? (
                          <span className="bdg bdg-remise">
                            −{remise}%
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-3 text-center text-gray-700">{service.quantity || 1}</td>
                      <td className="py-3 text-right font-semibold text-green-700">
                        {formatCurrency(service.total_detail || (finalPrice * (service.quantity || 1)))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Summary */}
        <div className="border-t-2 border-gray-800 pt-6">
          <div className="flex justify-end">
            <div className="w-64 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Sous-total:</span>
                <span className="font-semibold text-gray-800">{formatCurrency(Math.abs(sale.sub_total || 0))}</span>
              </div>
              
              {Math.abs(sale.discount_amount || 0) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Remise {sale.discount_percentage ? `(${sale.discount_percentage}%)` : ''}:</span>
                  <span className="font-semibold text-red-600">-{formatCurrency(Math.abs(sale.discount_amount || 0))}</span>
                </div>
              )}

              <div className="flex justify-between text-lg font-bold border-t-2 border-gray-300 pt-3">
                <span className="text-gray-800">{sale.is_return ? 'Total remboursé:' : 'Total à payer:'}</span>
                <span className="text-gray-900">{formatCurrency(Math.abs(sale.grand_total || 0))}</span>
              </div>

              {sale.is_return ? (<>
                <div className="flex justify-between text-sm border-t border-gray-200 pt-2">
                  <span className="text-gray-600">Rendu en espèces:</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(Math.abs(sale.amount_payed || 0))}</span>
                </div>
                {(parseFloat(sale.return_credit_offset) || 0) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Déduit du crédit:</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(parseFloat(sale.return_credit_offset) || 0)}</span>
                  </div>
                )}
              </>) : (
              <div className="flex justify-between text-sm border-t border-gray-200 pt-2">
                <span className="text-gray-600">Montant payé:</span>
                <span className="font-semibold text-green-600">{formatCurrency(sale.amount_payed || 0)}</span>
              </div>
              )}

              {(sale.amount_change || 0) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Monnaie rendue:</span>
                  <span className="font-semibold text-blue-600">{formatCurrency(sale.amount_change || 0)}</span>
                </div>
              )}

              {(sale.credit || 0) > 0 && (
                <div className="flex justify-between text-sm bg-orange-50 -mx-4 px-4 py-2 rounded">
                  <span className="text-orange-700 font-semibold">Reste à payer:</span>
                  <span className="font-bold text-orange-700">{formatCurrency(sale.credit || 0)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-gray-300 text-center">
          <p className="text-sm text-gray-600 mb-2">Merci pour votre confiance!</p>
          <p className="text-xs text-gray-500">Ce reçu est généré électroniquement et valable sans signature</p>
          <p className="text-xs text-gray-400 mt-4 uppercase">{(company && company.nom) || 'Kipara'} © {new Date().getFullYear()}</p>
        </div>
      </div>

      {/* Print-specific styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          @page {
            margin: 1cm;
          }
        }
      `}</style>
    </div>
  );
};

export default SaleReceipt;
